import { vec4 } from 'gl-matrix'
import Program from 'nanogl/program'
import Texture2D from 'nanogl/texture-2d'

import gui from '@/webgl/dev/gui'
import { Gui } from '@/webgl/dev/gui/api'
import FPSLimiter from '@/webgl/FPSLimiter'
import { randomFloat } from '@/webgl/math'
import BaseEffect, { EffectDependency } from '@/webgl/postprocess/effects/base-effect'
import AssetDatabase from '@/webgl/resources/AssetDatabase'

type NoiseSettings = {
  x: number
  y: number
}

const MAX = 0.01

function preCode(displacement: Displacement) {
  let pre = /*glsl*/ `
  uniform sampler2D uNoise;
  uniform vec2 uAspectRatio;
  uniform vec4 uNoiseSettings;

  #define NoiseScale uNoiseSettings.x
  #define DisplaceStrength uNoiseSettings.y
  #define NoiseOffset uNoiseSettings.zw

  `

  if (displacement.useFrozen) {
    pre += /*glsl*/ `
    #define HAS_FROZEN 1
    uniform sampler2D uFrozenTexture;
    uniform vec4 uFrozenSettings;
    #define FrozenScale uFrozenSettings.x
    #define FrozenStrength uFrozenSettings.y
    #define FrozenReveal uFrozenSettings.z
    `
  }

  return pre
}

function fragCode(displacement: Displacement) {
  return /*glsl*/ `
  vec2 offset = vec2(0.0, 0.0);

  #ifdef HAS_FROZEN
  vec3 froze = texture2D(uFrozenTexture, texCoordVP * uAspectRatio * FrozenScale).xyz;

  float dst = distance((texCoordFull * uAspectRatio), vec2(0.5) * uAspectRatio) + (froze.x - 0.5) * 2.0 * 0.05;
  float freveal = smoothstep(0.0, 0.15, dst - (1.0 - FrozenReveal));

  offset = (froze.xy - vec2(0.5)) * 2.0 * FrozenStrength;
  offset *= freveal;
  float light = dot(froze, normalize(vec3(-0.2, -0.8, 0.0))) * 0.5 * freveal;
  #endif

  vec3 n = texture2D(uNoise, texCoordFull * uAspectRatio * NoiseScale + NoiseOffset).rgb;
  vec2 sourceCoord = ((texCoordVP - vec2(0.5)) * (1.0 - ${MAX})) + vec2(0.5);
  vec2 noise = (n.xy - vec2(0.5)) * DisplaceStrength;

  vec2 tCoord = sourceCoord + noise + offset;
  c = texture2D(tInput,tCoord).xyz;

  #ifdef HAS_FROZEN
  c *= mix(vec3(1.0), vec3(0.92, 0.92, 1.0), freveal);
  c += vec3(light * light);
  #endif
  
  `
}

export default class Displacement extends BaseEffect {
  _flags: EffectDependency = EffectDependency.LINEAR

  limiter: FPSLimiter

  //#region Displace Settings
  textureNoise: Texture2D | null = null
  uniformNoise: vec4 = vec4.create()
  currentNoise: number = 0
  settingsNoise: NoiseSettings[] = []

  set noiseScale(v: number) {
    this.uniformNoise[0] = v
  }
  get noiseScale(): number {
    return this.uniformNoise[0]
  }

  set displaceStrength(v: number) {
    this.uniformNoise[1] = v
  }
  get displaceStrength(): number {
    return this.uniformNoise[1]
  }

  set offsetX(v: number) {
    this.uniformNoise[2] = v
  }
  get offsetX(): number {
    return this.uniformNoise[2]
  }

  set offsetY(v: number) {
    this.uniformNoise[3] = v
  }
  get offsetY(): number {
    return this.uniformNoise[3]
  }
  //#endregion

  //#region Frozen Settings
  useFrozen: boolean = false
  textureFrozen: Texture2D | null = null
  uniformFrozen: vec4 = vec4.create()
  set frozenScale(v: number) {
    this.uniformFrozen[0] = v
  }
  get frozenScale(): number {
    return this.uniformFrozen[0]
  }
  set frozenStrength(v: number) {
    this.uniformFrozen[1] = v
  }
  get frozenStrength(): number {
    return this.uniformFrozen[1]
  }
  set frozenReveal(v: number) {
    this.uniformFrozen[2] = v
  }
  get frozenReveal(): number {
    return this.uniformFrozen[2]
  }
  //#endregion

  constructor() {
    super()

    this.noiseScale = 1.0
    this.displaceStrength = 0.0097826087
    for (let i = 0; i < 20; i++) {
      const x = randomFloat(-1, 1)
      const y = randomFloat(-1, 1)
      this.settingsNoise.push({ x, y })
    }

    this.frozenScale = 2
    this.frozenReveal = 0
    this.frozenStrength = 0.01

    this.limiter = new FPSLimiter()
    this.limiter.enabled = true
    this.limiter.fps = 5
  }

  //#region GUI
  folder: Gui
  setupGUI(root: Gui) {
    this.folder = root ? root.folder('Displacement') : gui.folder('Displacement')
    this.folder.add(this, 'noiseScale', { min: 0, max: 2.0 })
    this.folder.add(this, 'displaceStrength', { min: 0, max: 0.1 })
    this.folder.add(this, 'offsetX', { min: -1, max: 1 })
    this.folder.add(this.limiter, 'fps', { min: 0, max: 60 }).setLabel('FPS')
    this.folder.add(this, 'frozenScale', { min: 0, max: 4 })
    this.folder.add(this, 'frozenStrength', { min: 0, max: 0.1 })
    this.folder.add(this, 'frozenReveal', { min: 0, max: 1 })
  }
  //#endregion

  genCode(precode: string[], code: string[]): void {
    precode.push(preCode(this))
    code.push(fragCode(this))
  }

  enableFrozen() {
    this.post._shaderInvalid = true
    this.useFrozen = true
  }

  disableFrozen() {
    this.post._shaderInvalid = true
    this.useFrozen = false
  }

  async load(): Promise<void> {
    this.textureNoise = await AssetDatabase.getTexture('noise/T_DisplacementNoise001.jpg', this.post.gl).load()
    if (this.useFrozen)
      this.textureFrozen = await AssetDatabase.getTexture(
        'noise/Frozen_Bake1_CyclesBake_COMBINED.webp',
        this.post.gl
      ).load()
  }

  public update(dt: number) {
    if (this.limiter.step(dt)) {
      this.currentNoise = (this.currentNoise + 1) % this.settingsNoise.length
      this.offsetX = this.settingsNoise[this.currentNoise].x
      this.offsetY = this.settingsNoise[this.currentNoise].y
    }
  }

  setupProgram(prg: Program): void {
    const bw = this.post!.bufferWidth
    const bh = this.post!.bufferHeight

    prg.uNoise(this.textureNoise)
    prg.uAspectRatio(bw / bh, 1)
    prg.uNoiseSettings(this.uniformNoise)

    if (this.useFrozen) {
      prg.uFrozenSettings(this.uniformFrozen)
      prg.uFrozenTexture(this.textureFrozen)
    }
  }

  init(): void {}
  release(): void {}
  preRender(): void {}
  resize(w: number, h: number): void {}
}
